import { useEffect } from "react";

type MetaInput = {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: object | object[];
  keywords?: string;
  noindex?: boolean;
  hreflang?: boolean;
};

const MANAGED_ATTR = "data-zeastro-seo";

const CANONICAL_ORIGIN = "https://zeastro.com";

// The app-wide defaults baked into index.html — used to restore the head
// whenever a route that doesn't own SEO metadata is entered client-side.
const DEFAULT_TITLE = "ZEASTRO AI | AI-Powered Vedic Astrology | Kundali, Birth Chart, Compatibility";
const DEFAULT_DESCRIPTION =
  "ZEASTRO AI — India's #1 AI-powered astrology platform. Free Kundali matching, birth chart analysis, tarot readings, numerology & cosmic compatibility. 9 Navagraha AI agents. Available in 50+ Indian & global languages. Try free today.";
const DEFAULT_OG_IMAGE = `${CANONICAL_ORIGIN}/opengraph.jpg`;

// Pathname (no query string) that the metadata currently in <head> was set
// for by the last applySeo() call. The router-level guard below compares
// this against the freshly-mounted route's location to detect a page that
// doesn't claim SEO ownership, so it can reset stale title/canonical/robots
// left behind by whatever page was visited before it.
let claimedForPath: string | null = null;

function setOrCreate(selector: string, create: () => HTMLElement, apply: (el: HTMLElement) => void) {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el) {
    el = create();
    el.setAttribute(MANAGED_ATTR, "1");
    document.head.appendChild(el);
  }
  apply(el);
}

export function applySeo(input: MetaInput) {
  if (typeof window !== "undefined") {
    claimedForPath = window.location.pathname;
  }
  const path = typeof window !== "undefined"
    ? window.location.pathname + window.location.search
    : "";
  const fullCanonical = input.canonical
    ? input.canonical.startsWith("http") ? input.canonical : `${CANONICAL_ORIGIN}${input.canonical}`
    : `${CANONICAL_ORIGIN}${path}`;
  const ogImage = input.ogImage || `${CANONICAL_ORIGIN}/og-default.png`;
  const ogType = input.ogType || "article";

  document.title = input.title;

  setOrCreate('meta[name="description"]', () => Object.assign(document.createElement("meta"), { name: "description" }), (el) => (el as HTMLMetaElement).content = input.description);
  if (input.keywords) {
    setOrCreate('meta[name="keywords"]', () => Object.assign(document.createElement("meta"), { name: "keywords" }), (el) => (el as HTMLMetaElement).content = input.keywords!);
  }

  setOrCreate('link[rel="canonical"]', () => { const l = document.createElement("link"); l.rel = "canonical"; return l; }, (el) => (el as HTMLLinkElement).href = fullCanonical);

  // robots noindex
  setOrCreate('meta[name="robots"]', () => Object.assign(document.createElement("meta"), { name: "robots" }), (el) => (el as HTMLMetaElement).content = input.noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");

  // hreflang for India multi-language SEO (default true for SEO pages).
  // Always clear any previously-added alternates first — otherwise a page
  // that opts out (noindex, or hreflang: false) inherits stale alternate
  // links left behind by whatever route was visited before it.
  document.head.querySelectorAll(`link[rel="alternate"][${MANAGED_ATTR}]`).forEach(s => s.remove());
  if (input.hreflang !== false && !input.noindex) {
    for (const lang of ["en", "hi", "ta", "te", "bn", "mr", "kn", "gu", "x-default"]) {
      const l = document.createElement("link");
      l.rel = "alternate"; l.hreflang = lang; l.href = fullCanonical;
      l.setAttribute(MANAGED_ATTR, "1");
      document.head.appendChild(l);
    }
  }

  const ogTags: [string, string][] = [
    ["og:title", input.title],
    ["og:description", input.description],
    ["og:type", ogType],
    ["og:url", fullCanonical],
    ["og:image", ogImage],
    ["og:site_name", "ZeAstro AI"],
  ];
  for (const [prop, val] of ogTags) {
    setOrCreate(`meta[property="${prop}"]`, () => { const m = document.createElement("meta"); m.setAttribute("property", prop); return m; }, (el) => (el as HTMLMetaElement).content = val);
  }

  const twTags: [string, string][] = [
    ["twitter:card", "summary_large_image"],
    ["twitter:title", input.title],
    ["twitter:description", input.description],
    ["twitter:image", ogImage],
  ];
  for (const [name, val] of twTags) {
    setOrCreate(`meta[name="${name}"]`, () => Object.assign(document.createElement("meta"), { name }), (el) => (el as HTMLMetaElement).content = val);
  }

  document.head.querySelectorAll(`script[type="application/ld+json"][${MANAGED_ATTR}]`).forEach((s) => s.remove());
  const lds = Array.isArray(input.jsonLd) ? input.jsonLd : input.jsonLd ? [input.jsonLd] : [];
  for (const ld of lds) {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute(MANAGED_ATTR, "1");
    s.text = JSON.stringify(ld);
    document.head.appendChild(s);
  }
}

/**
 * Restores the document head to the site-wide default metadata (the same
 * values baked into index.html). Used by useSeoRouteGuard when the route
 * that just mounted doesn't own any page-specific SEO metadata, so a title,
 * canonical, or robots tag from a previously-visited SEO page can't survive
 * a client-side navigation.
 */
export function resetSeo() {
  applySeo({
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
    noindex: false,
    hreflang: false,
  });
}

/**
 * Mount once near the app root (above the route switch) with the current
 * router location. After every SPA navigation, checks whether the page that
 * just mounted for that location called applySeo() itself; if not, resets
 * the head to the app defaults instead of leaving the previous route's
 * title/canonical/robots metadata in place.
 *
 * Relies on effects firing child-before-parent within a single commit: a
 * page's own `applySeo` call (in its own effect) runs before this guard's
 * effect, so a page that claims ownership this render is never overwritten.
 */
export function useSeoRouteGuard(location: string) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (claimedForPath !== location) {
      resetSeo();
    }
  }, [location]);
}

export function makeBreadcrumbLd(crumbs: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url.startsWith("http") ? c.url : `${CANONICAL_ORIGIN}${c.url}`,
    })),
  };
}

export function makeFaqLd(faqs: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function makeArticleLd(args: { title: string; description: string; url: string; datePublished?: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: args.title,
    description: args.description,
    url: args.url.startsWith("http") ? args.url : `${CANONICAL_ORIGIN}${args.url}`,
    datePublished: args.datePublished || new Date().toISOString().split("T")[0],
    author: { "@type": "Organization", name: "ZEASTRO AI" },
    publisher: {
      "@type": "Organization",
      name: "ZEASTRO AI",
      logo: { "@type": "ImageObject", url: `${CANONICAL_ORIGIN}/brand/icon-512.png` },
    },
    image: `${CANONICAL_ORIGIN}/og-default.png`,
  };
}
